// Modelos del marketplace público (Fase 1 MVP). Espejo simplificado de las
// tablas Mkt* (ver documentation/specs/SPEC-014). Hoy alimentados por mock;
// luego por api-nest (/v350/marketplace/*, SPEC-016).

export type Rubro = 'spa' | 'dentista' | 'manicura' | 'barberia';

/** Estado operativo publicado de una sede (GET /marketplace/sede/:idSede/estado). */
export type EstadoSedeCodigo = 'ABIERTA' | 'CERRADA' | 'NO_PUBLICADA';
export type DisponibilidadSede = 'DISPONIBLE' | 'OCUPADA';
/** Forzado manual desde el panel del dueño. Superficie de proveedor: acá se ignora. */
export type OverrideSede = 'ABIERTA' | 'CERRADA' | 'PAUSA';

/** Salas/boxes de la sede en el momento consultado. */
export interface CapacidadSede {
  total: number;
  ocupadas: number;
  libres: number;
}

/**
 * Estado operativo de una sede tal como lo calcula el backend. Viene embebido en cada
 * sede de `GET /marketplace/marca/:slug` y suelto en `GET /marketplace/sede/:idSede/estado`
 * (este último sólo para refrescar).
 *
 * `etiqueta` ya viene armada por el servidor — depende de zona horaria y de reglas de
 * negocio que no viven en el cliente — así que se pinta tal cual y NUNCA se reconstruye acá.
 */
export interface EstadoSede {
  idSede: number;
  estado: EstadoSedeCodigo;
  disponibilidad: DisponibilidadSede | null;
  motivo: string | null;
  /** ISO local hasta cuándo vale el estado (p.ej. fin del cierre). */
  hasta: string | null;
  /**
   * La sede ya no vuelve a abrir hoy. Distinto de `estado === 'CERRADA'`, que es sólo
   * "cerrada en este instante" y también aplica a una sede que abre más tarde hoy.
   * Backends viejos no lo mandan → llega `undefined` y se trata como "no sé" (no se avisa).
   */
  cerradaHoy: boolean;
  /** Hora a la que abre HOY si todavía va a abrir (`"08:00"`); `null` si ya no abre. */
  abreEn: string | null;
  /** Forzado manual del dueño. Sólo le sirve al panel de proveedor; esta app no lo usa. */
  override: OverrideSede | null;
  capacidad: CapacidadSede;
  /** Texto listo para pintar, p.ej. "Abierto · 2 de 3 salas libres". */
  etiqueta: string;
}

/** Tono visual del chip de estado. `null` = no hay dato → no se pinta chip. */
export type EstadoSedeTono = 'ok' | 'busy' | 'off';

/**
 * Tono del chip a partir del estado. Devuelve `null` cuando no hay estado (backend sin
 * la migración aplicada, o degradado) o cuando el servidor no mandó etiqueta: en esos
 * casos no se muestra nada — nunca se asume "Abierto" por defecto.
 */
export function tonoEstadoSede(e?: EstadoSede | null): EstadoSedeTono | null {
  if (!e || !e.estado || !e.etiqueta || !e.etiqueta.trim()) return null;
  if (e.estado !== 'ABIERTA') return 'off';
  return e.disponibilidad === 'OCUPADA' ? 'busy' : 'ok';
}

export interface Servicio {
  id: number;
  nombre: string;
  descripcion?: string;
  duracionMin: number;
  precioBruto: number; // soles
  /** % del bruto requerido para reservar (adelanto). */
  adelantoPct: number;
}

export interface Sede {
  id: number;
  slug: string;
  nombre: string; // p.ej. "Miraflores"
  direccion: string;
  distrito: string;
  horario: string; // "Lun–Sáb 9:00–20:00"
  telefono?: string;
  lat?: number;
  lng?: number;
  servicios: Servicio[];
  /** Estado operativo publicado. Ausente si el backend todavía no lo expone. */
  estado?: EstadoSede;
}

export interface Marca {
  id: number;
  slug: string;
  nombre: string;
  rubro: Rubro;
  descripcion: string;
  /** color de marca para el avatar/inicial. */
  color: string;
  rating: number; // 0–5
  resenas: number;
  destacado?: boolean;
  sedes: Sede[];
}

export const RUBROS: { key: Rubro; label: string; icon: string }[] = [
  { key: 'spa', label: 'Spa & Bienestar', icon: '🌿' },
  { key: 'dentista', label: 'Dentistas', icon: '🦷' },
  { key: 'manicura', label: 'Manicura & Nails', icon: '💅' },
  { key: 'barberia', label: 'Barbería', icon: '💈' },
];
